// M2 ComuneRecommender — score a comune against the user's profile.
//
// We don't reinvent the country/region scoring — we score on the
// comune's own metrics (climate, infra, internet, cost) and then
// apply a persona-specific bonus that nudges the result toward the
// kinds of comuni a "family" / "nomad" / "sport" user would actually
// pick. The persona is detected once from quiz answers and threaded
// through here (cheap).
//
// Output mirrors RegionScore so the UI can reuse the same card shape.

import type { ComuneFull } from '@/data';
import type { Answers } from '@/features/quiz/types';
import type { TFn } from '@/features/quiz/derive';
import { detectPersona, PERSONAS, type PersonaCode } from './persona';

export interface ComuneScore {
  comune: ComuneFull;
  /** 0..1 final fit. */
  score: number;
  components: {
    climate: number;
    cost: number;
    infra: number;
    /** null = the dataset carries no broadband telemetry for this country. */
    internet: number | null;
  };
  /** Detected persona for this user, surfaced in the UI as a badge. */
  persona: PersonaCode | null;
  /** Up to 3 short reasons. */
  reasons: string[];
}

const COMPONENT_FLOOR = 0.05;

function clamp01(x: number): number {
  return Math.max(0, Math.min(1, x));
}

function lerp(value: number, best: number, worst: number): number {
  if (best === worst) return 0.5;
  return clamp01((value - worst) / (best - worst));
}

/**
 * Mean over the terms that actually have data. A `null` term is dropped from
 * both the sum and the count, so a missing metric is never read as "scored
 * zero on this axis" — it just doesn't take part. Null if nothing is known.
 */
function meanAvailable(values: Array<number | null>): number | null {
  let sum = 0;
  let n = 0;
  for (const v of values) {
    if (v === null) continue;
    sum += v;
    n++;
  }
  return n > 0 ? sum / n : null;
}

function scoreClimate(c: ComuneFull, answers: Answers): number {
  const picks = answers.climate?.optionIds ?? [];
  const dealbreakers = answers.dealbreakers?.optionIds ?? [];
  let s = 0.5;

  // snow / sunny / hum are Italy-only columns today. Where they are absent the
  // term drops out of the mean (denominator shrinks) instead of contributing a
  // zero, which would silently read as "no snow at all" / "never sunny".
  if (picks.includes('alps')) {
    const alp = meanAvailable([
      lerp(c.alt, 1500, 0),
      lerp(-c.climate.t_jul, -18, -32),
      c.climate.snow != null ? lerp(c.climate.snow, 60, 0) : null,
    ]);
    if (alp !== null) s = Math.max(s, alp);
  }
  if (picks.includes('mediterranean')) {
    const med = meanAvailable([
      lerp(c.climate.t_jul, 28, 18),
      lerp(c.climate.t_jan, 8, -2),
      c.climate.sunny != null ? lerp(c.climate.sunny, 220, 100) : null,
    ]);
    if (med !== null) s = Math.max(s, med);
  }
  if (picks.includes('continental')) {
    s = Math.max(s, (lerp(c.climate.t_jul, 24, 32) + lerp(-c.climate.t_jan, 5, 8)) / 2);
  }
  // A dealbreaker penalty is an assertion about the place ("it snows a lot
  // here"). With no snow/humidity statistics we cannot assert it, so no penalty
  // is applied — inventing one would be as wrong as inventing a zero.
  if (dealbreakers.includes('hot-summer') && c.climate.t_jul > 30) s *= 0.5;
  if (dealbreakers.includes('snow') && c.climate.snow != null && c.climate.snow > 40) s *= 0.7;
  if (dealbreakers.includes('humidity') && c.climate.hum != null && c.climate.hum > 75) s *= 0.7;
  return clamp01(s);
}

function scoreCost(c: ComuneFull, answers: Answers): number {
  const bucket = answers.budget?.budgetBucket ?? 3;
  const rent = c.econ?.rent_eur_m2y;
  if (rent == null) return 0.5;
  const targets: Record<number, [number, number]> = {
    1: [60, 150],
    2: [80, 170],
    3: [120, 200],
    4: [180, 250],
  };
  const [best, worst] = targets[bucket] ?? targets[3];
  return lerp(-rent, -best, -worst);
}

function scoreInfra(c: ComuneFull, _answers: Answers, persona: PersonaCode | null): number {
  const bonus = persona ? PERSONAS[persona].bonus : null;
  const wSchool = bonus?.school_proximity ?? 1;
  const wHealth = bonus?.health_proximity ?? 1;
  const wTransit = bonus?.transit_proximity ?? 1;
  const wOutdoor = bonus?.outdoor_proximity ?? 1;

  const sSchool = lerp(-c.infra.school, -1, -15);
  const sHealth = (lerp(-c.infra.hosp, -2, -40) + lerp(-c.infra.pharm, -1, -10)) / 2;
  const sTransit = (lerp(-c.infra.train, -2, -30) + lerp(-c.infra.air, -10, -150)) / 2;

  // Ski-lift distance exists only where there is an alpine dataset. Without it
  // the outdoor axis is removed from the weighted average — both from the
  // numerator and from totalW — rather than scored 0 (= "no mountains here").
  const parts: Array<{ w: number; v: number }> = [
    { w: wSchool, v: sSchool },
    { w: wHealth, v: sHealth },
    { w: wTransit, v: sTransit },
  ];
  if (c.infra.ski != null) parts.push({ w: wOutdoor, v: lerp(-c.infra.ski, -3, -50) });

  const totalW = parts.reduce((acc, p) => acc + p.w, 0);
  return parts.reduce((acc, p) => acc + p.v * p.w, 0) / totalW;
}

function scoreInternet(c: ComuneFull, answers: Answers, persona: PersonaCode | null): number | null {
  // No broadband telemetry for this country → the component is excluded from
  // the final mean (see geometricMean), not scored 0.
  if (c.internet == null) return null;
  const incomes = answers.income?.optionIds ?? [];
  const dl = c.internet.dl;
  const base = lerp(dl, 150, 20);
  const internetMul = persona ? PERSONAS[persona].bonus.internet : 1;
  const weighted = incomes.includes('remote') || internetMul > 1.2 ? base : 0.5 + base * 0.3;
  return clamp01(weighted * Math.min(internetMul, 1));
}

function applyQuietBias(score: number, c: ComuneFull, persona: PersonaCode | null): number {
  if (!persona) return score;
  const bias = PERSONAS[persona].bonus.quiet_bias;
  if (bias === 1) return score;
  // pop > 50k = urban; pop < 5k = quiet
  const small = c.pop < 5000;
  const large = c.pop > 50000;
  if (bias > 1 && small) return score * Math.min(bias, 1.15);
  if (bias > 1 && large) return score * 0.95;
  if (bias < 1 && large) return score * (1 + (1 - bias) * 0.5);
  if (bias < 1 && small) return score * 0.95;
  return score;
}

// Components with no data are skipped — they shrink the denominator instead of
// dragging the geometric mean down to the COMPONENT_FLOOR.
function geometricMean(comp: ComuneScore['components']): number {
  let logSum = 0;
  let n = 0;
  for (const v of Object.values(comp)) {
    if (v === null) continue;
    logSum += Math.log(Math.max(v, COMPONENT_FLOOR));
    n++;
  }
  return n > 0 ? Math.exp(logSum / n) : 0;
}

// ── Reasons ────────────────────────────────────────────────────────────────
// Goals (revised after the user feedback "every comune just shows 'Strong
// internet: XXX Mbps'"):
//   1. Emit 2–3 reasons per comune, not 1 — generic infra + climate
//      should appear alongside internet.
//   2. Internet must not always rank first. Italy's fiber coverage is high
//      enough that most comuni clear the old 0.7 threshold trivially, so
//      internet "wins" by default. Fix: downweight internet's strength and
//      lower thresholds for the other dimensions so they can compete.
//   3. Always include at least one climate or cost reason if available —
//      these are what users actually care about when picking a town.
//
// Mechanism: each candidate reason gets a "strength" score = how strongly
// this comune differentiates on that axis. Climate and cost are rewarded
// extra weight when they match the user's specific picks. Internet's
// strength is multiplied by 0.6 so it only wins when truly exceptional.
function topReasons(
  c: ComuneFull,
  comp: ComuneScore['components'],
  persona: PersonaCode | null,
  answers: Answers,
  t: TFn,
): string[] {
  type Candidate = { kind: 'climate' | 'cost' | 'infra' | 'internet'; strength: number; text: string };
  const out: Candidate[] = [];

  const picks = answers.climate?.optionIds ?? [];

  // Climate — generate a specific reason matched to the user's pick.
  // Use a softer threshold (0.55) so climate frequently shows up.
  if (comp.climate >= 0.55) {
    if (picks.includes('alps') && c.alt > 800) {
      out.push({
        kind: 'climate',
        strength: comp.climate + 0.15, // boost: matches user's explicit pick
        text: t('reco.reason.alpineClimate', { alt: Math.round(c.alt) }),
      });
    } else if (picks.includes('mediterranean') && c.climate.t_jul >= 26) {
      out.push({
        kind: 'climate',
        strength: comp.climate + 0.15,
        text: t('reco.reason.mediterraneanWarmth', { temp: c.climate.t_jul.toFixed(1) }),
      });
    } else if (picks.includes('continental') && c.climate.t_jan < 5) {
      out.push({
        kind: 'climate',
        strength: comp.climate + 0.1,
        text: t('reco.reason.continentalSeasons', {
          tJan: c.climate.t_jan.toFixed(1),
          tJul: c.climate.t_jul.toFixed(1),
        }),
      });
    } else if (c.climate.sunny != null) {
      out.push({
        kind: 'climate',
        strength: comp.climate,
        text: t('reco.reason.climateAvg', {
          temp: c.climate.t_mean.toFixed(1),
          sunny: c.climate.sunny,
        }),
      });
    } else {
      // Страна без статистики солнечных дней. Раньше здесь не выдавалась
      // климатическая причина вообще — то есть гарантия «хотя бы одна причина
      // про климат или цену» молча закрывалась только ценой. Отдельная фраза
      // по двум температурам честна и ничего не выдумывает.
      out.push({
        kind: 'climate',
        strength: comp.climate,
        text: t('reco.reason.climateAvgTemp', {
          temp: c.climate.t_mean.toFixed(1),
          tJul: c.climate.t_jul.toFixed(1),
        }),
      });
    }
  }

  // Cost — rent data is optional but when present and competitive, surface it.
  // Lower threshold (0.55) + boost when the user picked a tight budget.
  if (comp.cost >= 0.55 && c.econ?.rent_eur_m2y != null) {
    const bucket = answers.budget?.budgetBucket ?? 3;
    const costBoost = bucket <= 2 ? 0.15 : 0;
    out.push({
      kind: 'cost',
      strength: comp.cost + costBoost,
      text: t('reco.reason.affordable', { rent: c.econ.rent_eur_m2y }),
    });
  }

  // Infra — pick the most informative variant for this persona.
  if (comp.infra >= 0.55) {
    if (persona === 'family') {
      out.push({
        kind: 'infra',
        strength: comp.infra + 0.1, // persona-aligned
        text: t('reco.reason.familyReady', {
          school: c.infra.school.toFixed(1),
          hospital: c.infra.hosp.toFixed(1),
        }),
      });
    } else if (persona === 'sport' && c.infra.ski != null) {
      // No ski-lift data → fall through to the generic infra line below.
      out.push({
        kind: 'infra',
        strength: comp.infra + 0.1,
        text: t('reco.reason.outdoorAccess', { ski: c.infra.ski.toFixed(1) }),
      });
    } else if (persona === 'nomad' || persona === 'urban') {
      out.push({
        kind: 'infra',
        strength: comp.infra + 0.05,
        text: t('reco.reason.transitAccess', {
          train: c.infra.train.toFixed(1),
          airport: c.infra.air.toFixed(0),
        }),
      });
    } else {
      out.push({
        kind: 'infra',
        strength: comp.infra,
        text: t('reco.reason.infrastructureAccess', {
          hospital: c.infra.hosp.toFixed(1),
          train: c.infra.train.toFixed(1),
        }),
      });
    }
  }

  // Internet — DOWNWEIGHTED so it stops winning every slot. Multiplier 0.6
  // means internet only ranks first when its component is exceptionally high
  // AND no climate / cost / infra reason fired. Still informative when the
  // user's quiz answer made internet matter (remote work).
  const incomes = answers.income?.optionIds ?? [];
  const internetMultiplier = incomes.includes('remote') ? 0.9 : 0.6;
  if (comp.internet !== null && comp.internet >= 0.7 && c.internet != null) {
    out.push({
      kind: 'internet',
      strength: comp.internet * internetMultiplier,
      text: t('reco.reason.strongInternet', { mbps: Math.round(c.internet.dl) }),
    });
  }

  // Re-rank by strength descending.
  out.sort((a, b) => b.strength - a.strength);

  // Guarantee at least one climate or cost reason in the top 3 when available.
  // If the top-3 slice has none, swap the weakest top-3 with the strongest
  // climate / cost candidate further down the list. Mirrors the "always
  // surface what users actually care about" principle from the feedback.
  const topThree = out.slice(0, 3);
  const hasAnchor = topThree.some((r) => r.kind === 'climate' || r.kind === 'cost');
  if (!hasAnchor) {
    const anchor = out.slice(3).find((r) => r.kind === 'climate' || r.kind === 'cost');
    if (anchor && topThree.length > 0) {
      topThree[topThree.length - 1] = anchor;
    } else if (anchor) {
      topThree.push(anchor);
    }
  }

  return topThree.map((r) => r.text);
}

export function scoreComune(
  comune: ComuneFull,
  answers: Answers,
  t: TFn,
  persona?: PersonaCode | null,
): ComuneScore {
  const p = persona ?? detectPersona(answers);
  const components = {
    climate: scoreClimate(comune, answers),
    cost: scoreCost(comune, answers),
    infra: scoreInfra(comune, answers, p),
    internet: scoreInternet(comune, answers, p),
  };
  let score = geometricMean(components);
  score = applyQuietBias(score, comune, p);
  return {
    comune,
    score: clamp01(score),
    components,
    persona: p,
    reasons: topReasons(comune, components, p, answers, t),
  };
}

export function scoreComuni(
  comuni: ComuneFull[],
  answers: Answers,
  t: TFn,
  topN = 10,
): ComuneScore[] {
  const persona = detectPersona(answers);
  return comuni
    .map((c) => scoreComune(c, answers, t, persona))
    .sort((a, b) => b.score - a.score)
    .slice(0, topN);
}
